import { h } from '../lib/dom.js';
import { getState, fmtNum, fmtReporting, toReporting, totalUSDFor, totalReportingAll, CONFIG, explorerAddressUrl } from '../store.js';
import { iconMarkup } from '../icons/index.js';
import { t } from '../i18n/index.js';
import {
  openOpenAccount, setAccountSort, goTransfer,
  startHomeEdit, setHomeEditDraft, confirmHomeEdit, cancelHomeEdit,
  toggleBalanceInfo
} from '../actions.js';
import { renderActivityRail } from './activityRail.js';

const ACCOUNT_SORTS = {
  'name-asc': (a, b) => a.name.localeCompare(b.name),
  'name-desc': (a, b) => b.name.localeCompare(a.name),
  'balance-desc': (a, b) => totalUSDFor(b) - totalUSDFor(a),
  'balance-asc': (a, b) => totalUSDFor(a) - totalUSDFor(b)
};

function sortIcons(s) {
  const nameIcon = s.accountSort === 'name-desc' ? 'arrowDownZA' : 'arrowDownAZ';
  const balanceIcon = s.accountSort === 'balance-asc' ? 'arrowDown01' : 'arrowDown10';
  const nameActive = s.accountSort.startsWith('name-');
  const balanceActive = s.accountSort.startsWith('balance-');
  return h('div', { class: 'flex-gap-sm' }, [
    h('button', {
      class: 'icon-btn', 'aria-label': t('home.sortByName'),
      onclick: () => setAccountSort('name')
    }, h('span', { html: iconMarkup(nameIcon, { size: 16, color: nameActive ? '#4D4DFF' : '#757993' }) })),
    h('button', {
      class: 'icon-btn', 'aria-label': t('home.sortByBalance'),
      onclick: () => setAccountSort('balance')
    }, h('span', { html: iconMarkup(balanceIcon, { size: 16, color: balanceActive ? '#4D4DFF' : '#757993' }) }))
  ]);
}

function editableField(s, w, field, className) {
  const editing = s.homeEdit.accountId === w.id && s.homeEdit.field === field;
  if (editing) {
    return h('div', { class: 'field-row' }, [
      h('input', {
        value: s.homeEdit.draft,
        oninput: (e) => setHomeEditDraft(e.target.value),
        onkeydown: (e) => { if (e.key === 'Enter') confirmHomeEdit(); if (e.key === 'Escape') cancelHomeEdit(); }
      }),
      h('button', { class: 'btn btn-primary btn-sm', onclick: confirmHomeEdit }, t('common.save')),
      h('button', { class: 'btn btn-secondary btn-sm', onclick: cancelHomeEdit }, t('common.cancel'))
    ]);
  }
  let value = w.owner || '—';
  if (field === 'name') value = w.name;
  // feature-harden.md 6.6: per-account send limit, editable the same
  // inline way name/owner already are — "—" reads as "no limit set".
  else if (field === 'sendLimit') value = w.sendLimit == null ? t('home.noSendLimit') : t('home.sendLimit', { limit: fmtReporting(w.sendLimit) });
  return h('span', { class: className, onclick: () => startHomeEdit(w.id, field) }, value);
}

// The explorer-link icon needs an already-derived address (see
// ensureHomeAddresses/main.js, populated once at boot) — until it lands,
// the icon renders inert (href="#", click prevented) rather than not at
// all, so the row layout doesn't shift once the address resolves.
function balanceExplorerLink(s, w, ccy) {
  const address = s.homeAddresses[w.id + '|' + ccy];
  const chain = CONFIG.NET[ccy] || '';
  const href = explorerAddressUrl(chain, address, s.testnetEnabled);
  return h('a', {
    class: 'icon-btn-plain', 'aria-label': t('home.addressOnChain', { ccy, chain }), title: chain,
    href: href || '#',
    target: href ? '_blank' : undefined, rel: href ? 'noopener noreferrer' : undefined,
    onclick: (e) => { e.stopPropagation(); if (!href) e.preventDefault(); }
  }, h('span', { html: iconMarkup('link', { size: 13, color: '#757993' }) }));
}

function accountCard(s, w) {
  const balanceRows = Object.entries(w.balances).map(([ccy, amt]) =>
    h('div', { class: 'balance-row balance-row-clickable', onclick: () => goTransfer(w.id, ccy) }, [
      h('span', {}, [
        `${ccy}  ${fmtNum(amt)}`,
        w.balancesLoading && w.balancesLoading[ccy]
          ? h('span', { class: 'balance-stale' }, t('home.updating'))
          : null
      ]),
      h('span', { class: 'flex-gap-sm items-center' }, [
        h('span', { class: 'equiv' }, fmtReporting(toReporting(ccy, amt))),
        balanceExplorerLink(s, w, ccy)
      ])
    ])
  );
  return h('div', { class: 'account-card' }, [
    h('div', { class: 'row-top' }, [
      editableField(s, w, 'name', 'name'),
      h('span', { class: 'total' }, fmtReporting(totalUSDFor(w)))
    ]),
    editableField(s, w, 'owner', 'owner'),
    editableField(s, w, 'sendLimit', 'send-limit'),
    h('div', { class: 'balances' }, balanceRows)
  ]);
}

function addAccountTile() {
  return h('div', { class: 'account-card account-card-add', onclick: openOpenAccount }, [
    h('span', { html: iconMarkup('circlePlus', { size: 28, color: '#4D4DFF' }) })
  ]);
}

export function renderHome() {
  const s = getState();

  const sortedWallets = ACCOUNT_SORTS[s.accountSort]
    ? [...s.wallets].sort(ACCOUNT_SORTS[s.accountSort])
    : s.wallets;

  const accountCards = sortedWallets.map((w) => accountCard(s, w));

  // .screen-layout/.screen-main/.screen-rail (style.css): stacked on
  // mobile/narrow-desktop exactly as before, a sticky two-column grid
  // (accounts left, recent activity right) once the viewport is wide
  // enough — feature-ui.md's Desktop section. renderActivityRail(true)
  // keeps this rail always rendered (not desktop-gated) since Home already
  // showed recent activity inline pre-Desktop; only its position changes.
  return h('div', { class: 'screen-layout' }, [
    h('div', { class: 'screen screen-main' }, [
      h('div', { class: 'flex-gap-sm items-center' }, [
        h('div', { class: 'balance-label' }, t('home.totalBalance', { currency: s.reportingCurrency })),
        h('button', { class: 'icon-btn-plain', 'aria-label': t('home.balanceInfoTitle'), onclick: toggleBalanceInfo },
          h('span', { html: iconMarkup('circleQuestionMark', { size: 13, color: '#757993' }) }))
      ]),
      s.showBalanceInfo ? h('div', { class: 'text-sm text-muted mb-sm' },
        t('home.balanceInfo', { currency: s.reportingCurrency })
      ) : null,
      h('div', { class: 'balance-hero' }, fmtReporting(totalReportingAll())),
      h('div', { class: 'flex-between' }, [
        h('div', { class: 'section-label' }, t('home.accounts')),
        s.wallets.length > 1 ? sortIcons(s) : null
      ]),
      h('div', { class: 'stack' }, [...accountCards, addAccountTile()])
    ]),
    renderActivityRail(true)
  ]);
}
